import { ChangeDetectorRef, Component, ComponentRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SnackBarService } from '@core/services/snackbar.service';
import { ArticleBlockComponent } from '@modules/knowledge/components/article-block/article-block.component';
import { Article } from '@modules/knowledge/models/article/article';
import { ArticleBlock } from '@modules/knowledge/models/article/article-block';
import { Manual } from '@modules/knowledge/models/manual/manual';
import { ArticleReadTimer } from '@modules/knowledge/services/article-read-timer';
import { ArticleService } from '@modules/knowledge/services/article.service';
import { ManualService } from '@modules/knowledge/services/manual.service';
import * as FileSaver from 'file-saver';
import diff from 'html-diff-ts';
import { Subscription, filter } from 'rxjs';

@Component({
    selector: 'app-article',
    templateUrl: './article.component.html',
    styleUrls: ['./article.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ArticleComponent implements OnChanges, OnDestroy {
  loading:boolean = true;
  summaryMode:boolean = true;
  showDiff:boolean = false;
  article!:Article;

  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
  diffHtml: string = "";

  @Input() articleId:string = '';
  @Input() slug:string = '';
  @Input() manualId:string = '';
  @Input() messageViewer = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastService:SnackBarService,
    private manualService:ManualService,
    private cdr: ChangeDetectorRef,
    private articleService:ArticleService,
    private articleReadTimer: ArticleReadTimer
  ){}


  private articleSubscription!: Subscription;
  showContentsTable:boolean = false;

  ngOnDestroy(): void {
    this.articleReadTimer.stop();
  }

  ngOnChanges(): void {
    this.loading = true;
    if(this.articleId || this.slug){

      if (this.articleSubscription) {
        this.articleSubscription.unsubscribe();
      }

      this.manualService.manual.pipe(filter((manual): manual is Manual => manual !== null)).subscribe(m =>{
        this.manualService.openTree(this.slug? this.slug:this.articleId);
      })
      let call = this.messageViewer ? this.articleService.getArticle(this.articleId): this.articleService.getManualArticle(this.articleId);
      if(this.slug && this.manualId){
        call = this.articleService.getManualArticleBySlug(this.manualId, this.slug);
      }
      this.articleSubscription = call.subscribe(x =>{
        this.article = x;
        this.loading = false;
        this.showDiff = false;
        this.summaryMode = x.summaryBlocks.length > 0;
        this.setContentsTable();
  
        this.articleReadTimer.start(this.article);
  
        const blockIndex = this.route.snapshot.queryParams['b'] ?? null;
        if(blockIndex !== null){
          if(blockIndex.split('-')[1]){
            this.summaryMode = blockIndex.split('-')[1] == 'summary';
          }
          setTimeout(() =>{
            this.scrollTo('.block-'+blockIndex.split('-')[0]);
          })
        }
  
      },error =>{
        console.log(error);
        this.toastService.error();
        this.router.navigate(['/knowledge/manuals']);
      })
    }
  }

  toggleDiff() {
    this.showDiff = !this.showDiff;
    this.generateDiff();
  }

  changeSummaryMode(b: boolean) {
    this.summaryMode = b;
    this.generateDiff();
    this.setContentsTable();
  }

  get hasSideContent(): boolean {
    return this.showContentsTable || this.article.attachments.length > 0 || (!this.messageViewer && this.article.relatedArticles.length > 0);
  }

  setContentsTable(){
    if(this.summaryMode){
      this.showContentsTable = this.article.summaryBlocks.filter(x => x.name).length > 0;
    }else{
      this.showContentsTable = this.article.contentBlocks.filter(x => x.name).length > 0;
    }
  }


  generateDiff() {
    if (this.showDiff) {
      const dateRegepx = /\d\d\/\d\d\/\d\d\d\d/gm;

      const originalBlocks = this.summaryMode ? this.article.previousSummaryBlocks : this.article.previousContentBlocks;
      const changedBlocks = this.summaryMode ? this.article.summaryBlocks : this.article.contentBlocks;

      let allHtml = '';
      originalBlocks.forEach((block: ArticleBlock, index: number) => {

        const changedBlock = changedBlocks[index];

        if (changedBlock) {
          if (block.type != changedBlock.type) {

            allHtml += this.createHTML(block, 'removed');
            allHtml += this.createHTML(changedBlock, 'added');

          } else {

            if (block.type == 'gallery' && block.imageColumnCount != changedBlock.imageColumnCount) {
              allHtml += this.createHTML(block, 'removed');
              allHtml += this.createHTML(changedBlock, 'added');
            } else {
              const originalHtml = this.createHTML(block);
              const changedHtml = this.createHTML(changedBlock);
              allHtml += diff(originalHtml, changedHtml, { blocksExpression: [{ exp: dateRegepx }] });
            }

          }
        } else {
          allHtml += this.createHTML(block, 'removed');
        }

      });

      if (changedBlocks.length > originalBlocks.length) {
        changedBlocks.slice(originalBlocks.length).forEach((block: ArticleBlock) => {
          allHtml += this.createHTML(block, 'added');
        });
      }

      this.diffHtml = allHtml;
    }
  }

  createHTML(block: ArticleBlock, className: string = "") {

    let allHtml = '';
    const componentRef: ComponentRef<ArticleBlockComponent> = this.container.createComponent(ArticleBlockComponent);
    componentRef.instance.block = block;
    this.cdr.detectChanges();

    const element = componentRef.location.nativeElement.firstChild;
    if (className) {
      element.classList.add(className);
    }
    let html = element.outerHTML;

    // Remove Angular-specific comments
    html = html.replace(/<!--[\s\S]*?-->/g, '');

    // Remove Angular-specific attributes
    html = html.replace(/\s?_ngcontent-[\w-]*=""/g, '');

    allHtml += html;
    componentRef.destroy();

    return allHtml;

  }


  scrollTo(selector:string){
    const target = document.querySelector(selector);
    if(target){
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
        });
    }
  }

  download(fileUrl:string){
    FileSaver.saveAs(fileUrl);
  }

}
